/// Port of PixelContainer / MainPixelContainer from
/// mobile/lib/ui/pixel/pixel_container.dart.
///
/// Flutter paints the chrome with a CustomPainter behind the child. The web
/// equivalent is an absolutely positioned canvas behind the content, repainted
/// from a ResizeObserver whenever the box changes size.

import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';

import { useThemeColors } from '@/settings/handler/settingsStore';
import { paintPixelBorder } from './pixelBorderPainter';

export interface PixelContainerProps {
  children: ReactNode;
  /** Corner radius in grid cells, not pixels. */
  radius?: number;
  pixelScale?: number;
  backgroundColor?: string;
  borderColor?: string;
  /** With bottomColor, fills the top and bottom halves differently. */
  topColor?: string;
  bottomColor?: string;
  onClick?: () => void;
  className?: string;
  style?: CSSProperties;
  padding?: string;
  ariaLabel?: string;
}

export function PixelContainer({
  children,
  radius = 2,
  pixelScale = 4,
  backgroundColor,
  borderColor,
  topColor,
  bottomColor,
  onClick,
  className = '',
  style,
  padding = '8px',
  ariaLabel,
}: PixelContainerProps) {
  const colors = useThemeColors();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);

  const fill = backgroundColor ?? colors.surfaceContainerHighest;
  const border = borderColor ?? colors.onSurface;

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = hostRef.current;
    if (!canvas || !host) return;

    const repaint = (): void => {
      const { width, height } = host.getBoundingClientRect();
      if (width < 1 || height < 1) return;

      // Backing store stays in CSS pixels: the grid is already whole pixels,
      // so scaling by devicePixelRatio would only blur the cell edges.
      const w = Math.round(width);
      const h = Math.round(height);
      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== h) canvas.height = h;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      paintPixelBorder(ctx, w, h, {
        radius,
        pixelScale,
        borderColor: border,
        fillColor: fill,
        topColor: topColor ?? null,
        bottomColor: bottomColor ?? null,
      });
    };

    repaint();
    const observer = new ResizeObserver(repaint);
    observer.observe(host);
    return () => observer.disconnect();
  }, [radius, pixelScale, border, fill, topColor, bottomColor]);

  const content = (
    <>
      <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 h-full w-full" />
      {/* Flutter adds pixelScale of inset before the caller's padding. */}
      <div className="relative" style={{ padding: `${pixelScale}px` }}>
        <div style={{ padding }}>{children}</div>
      </div>
    </>
  );

  // Keep `relative` for the absolute canvas, but let callers override via
  // `style.position` (Tailwind `absolute` in className can lose to `relative`).
  if (onClick) {
    return (
      <button
        ref={hostRef as unknown as React.RefObject<HTMLButtonElement>}
        type="button"
        aria-label={ariaLabel}
        onClick={onClick}
        className={`relative block text-left ${className}`}
        style={style}
      >
        {content}
      </button>
    );
  }

  return (
    <div ref={hostRef} className={`relative ${className}`} style={style}>
      {content}
    </div>
  );
}

/// Two-tone variant of PixelContainer (top / bottom fill).
export function MainPixelContainer({
  topColor,
  bottomColor,
  ...props
}: PixelContainerProps) {
  const colors = useThemeColors();
  const fallback = colors.surfaceContainerHighest;

  return (
    <PixelContainer
      {...props}
      backgroundColor={fallback}
      topColor={topColor ?? fallback}
      bottomColor={bottomColor ?? fallback}
    />
  );
}
